import { BrowserRouter as Router, Routes, Route, Outlet, Navigate, useNavigate } from 'react-router-dom';
import { useEffect, lazy, Suspense } from 'react';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { VoicenterProvider } from './contexts/VoicenterContext';
import { ProtectedRoute, AdminRoute, AuthCallback, ResetPassword } from './components/Auth';
import SuperAdminRoute from './components/Auth/SuperAdminRoute';
import CenterManagerRoute from './components/Auth/CenterManagerRoute';
import Layout from './components/Layout/Layout';
import BrowserTransitionHandler from './components/BrowserTransitionHandler';
import LogServiceInitializer from './components/LogServiceInitializer';
import { useSystemUpdate } from './hooks/useSystemUpdate';
import { SystemUpdateModal } from './components/SystemUpdateModal';
import { startMainThreadPressureObserver } from './utils/mainThreadPressure';
import RepresentativeStats from './pages/Admin/RepresentativeStats';
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const Calls = lazy(() => import('./pages/Calls/Calls'));
const CallDetails = lazy(() => import('./pages/Calls/CallDetails'));
const CostAnalysis = lazy(() => import('./pages/CostAnalysis/CostAnalysis'));
const UserManagement = lazy(() => import('./pages/Admin/UserManagement'));
const UserCallHistory = lazy(() => import('./pages/Admin/UserCallHistory'));
const UserDetails = lazy(() => import('./pages/Admin/UserDetails'));
const CenterManagement = lazy(() => import('./pages/Admin/CenterManagement'));
const AgentConfigManagement = lazy(() => import('./pages/Admin/AgentConfigManagement'));
const PendingAccess = lazy(() => import('./pages/PendingAccess/PendingAccess'));
const CallLogViewer = lazy(() => import('./pages/Admin/CallLogViewer'));
const CrmActivityViewer = lazy(() => import('./pages/Admin/CrmActivityViewer'));
const DownloadOptions = lazy(() => import('./pages/DownloadOptions/DownloadOptions'));
const Leads = lazy(() => import('./pages/Leads/Leads'));

const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
    <span className="inline-block h-10 w-10 border-2 border-neutral-400 border-t-transparent rounded-full animate-spin" />
  </div>
);


function AppContent() {
  const { isUpdateAvailable, remoteVersion } = useSystemUpdate();

  const { session } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    startMainThreadPressureObserver();
  }, []);

  useEffect(() => {
    // 1. Direct hash check on load
    if (window.location.hash.includes('type=recovery')) {
      console.log('[App] Recovery hash detected on load. Redirecting to reset-password.');
      navigate({
        pathname: '/auth/reset-password',
        hash: window.location.hash
      }, { replace: true });
    }

    // 2. Event listener for auth state changes
    const handleRecoveryEvent = () => {
      console.log('[App] Recovery event received. Redirecting to reset-password.');
      navigate({
        pathname: '/auth/reset-password',
        hash: window.location.hash
      }, { replace: true });
    };

    window.addEventListener('auth-password-recovery', handleRecoveryEvent);
    return () => window.removeEventListener('auth-password-recovery', handleRecoveryEvent);
  }, [navigate]);

  return (
    <>
    
      <BrowserTransitionHandler />
      <LogServiceInitializer />
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/reset-password" element={
              <ProtectedRoute skipApprovalCheck={true}>
                <ResetPassword />
              </ProtectedRoute>
            } />
            <Route path="/pending-access" element={
              <ProtectedRoute allowUnapproved={true}>
                <PendingAccess />
              </ProtectedRoute>
            } />
            <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
              <Route index element={<Dashboard />} />
              <Route path="settings" element={<Settings />} />
              <Route path="calls" element={<Calls />} />
              <Route path="calls/:id" element={<CallDetails />} />
              <Route path="cost-analysis" element={<CostAnalysis />} />
              <Route path="leads" element={<Leads />} />
              <Route path="downloads" element={<Navigate to="/settings?tab=downloads" replace />} />
              <Route path="download-options" element={<DownloadOptions />} />
              <Route path="admin" element={<AdminRoute><Outlet /></AdminRoute>}>
                <Route path="users" element={<UserManagement />} />
                <Route path="users/:userId/details" element={<UserDetails />} />
                <Route path="users/:userId/calls" element={<UserCallHistory />} />
                <Route path="centers" element={<SuperAdminRoute><CenterManagement /></SuperAdminRoute>} />
                <Route path="agent-config" element={<SuperAdminRoute><AgentConfigManagement /></SuperAdminRoute>} />
                <Route path="logs" element={<SuperAdminRoute><CallLogViewer /></SuperAdminRoute>} />
                <Route path="crm-activity" element={<SuperAdminRoute><CrmActivityViewer /></SuperAdminRoute>} />
                <Route path="representative-stats" element={<CenterManagerRoute><RepresentativeStats /></CenterManagerRoute>} />
              </Route>
            </Route>
          </Routes>
        </Suspense>
      </div>
      <SystemUpdateModal
        isOpen={isUpdateAvailable && !!session}
        version={remoteVersion}
      />
    </>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <VoicenterProvider>
          <Router>
            <AppContent />
          </Router>
        </VoicenterProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
